<template>
  <OverlayFrame
    :zIndex="overlayZIndex"
    placement="top"
    wide
    :title="title"
    :subtitle="subtitle"
    :icon="ICONS.actions.search"
    @close="emit('close')"
  >
    <div class="stack tight">
      <label class="sr-only" :for="inputId">Command search</label>
      <input
        :id="inputId"
        ref="inputRef"
        v-model="query"
        class="ui-input command-input stat-mono"
        type="text"
        autocomplete="off"
        autocapitalize="off"
        spellcheck="false"
        role="combobox"
        aria-expanded="true"
        aria-controls="command-list"
        :aria-activedescendant="activeDescId"
        :placeholder="placeholder"
        @keydown="handleKeydown"
      />

      <div id="command-list" class="command-list" role="listbox" aria-label="Commands">
        <button
          v-for="(cmd, index) in filtered"
          :id="descId(index)"
          :key="cmd.id"
          class="command-item"
          :class="{ 'is-active': index === activeIndex }"
          type="button"
          role="option"
          :aria-selected="index === activeIndex"
          @mouseenter="activeIndex = index"
          @click="select(cmd)"
        >
          <span class="command-item__icon" aria-hidden="true">{{ cmd.icon }}</span>
          <span class="command-item__text">
            <span class="command-item__label">{{ cmd.label }}</span>
            <span v-if="cmd.description" class="command-item__desc text-muted">{{ cmd.description }}</span>
          </span>
          <span v-if="cmd.hint" class="command-item__hint">{{ cmd.hint }}</span>
        </button>

        <div v-if="filtered.length === 0" class="command-empty text-muted" role="note">
          No matches.
        </div>
      </div>
    </div>
  </OverlayFrame>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from "vue";
import { ICONS } from "../../../lib/icons";
import OverlayFrame from "../../../components/overlay/OverlayFrame.vue";

export type CommandPaletteItem = {
  id: string;
  label: string;
  description?: string | null;
  icon: string;
  hint?: string | null;
  keywords?: string[];
};

const props = withDefaults(
  defineProps<{
    overlayZIndex?: number;
    title?: string;
    subtitle?: string;
    placeholder?: string;
    commands: CommandPaletteItem[];
  }>(),
  {
    overlayZIndex: 4000,
    title: "Command Palette",
    subtitle: "Type to search. Enter to run.",
    placeholder: "Search commands…"
  }
);

const emit = defineEmits<{
  close: [];
  select: [id: string];
}>();

const inputRef = ref<HTMLInputElement | null>(null);
const query = ref("");
const activeIndex = ref(0);
const inputId = `cmd_${Math.random().toString(16).slice(2)}`;

const filtered = computed(() => {
  const q = query.value.trim().toLowerCase();
  if (!q) return props.commands;
  return props.commands.filter((c) => {
    const hay = [c.label, c.description ?? "", ...(c.keywords ?? [])].join(" ").toLowerCase();
    return hay.includes(q);
  });
});

const activeDescId = computed(() => (filtered.value.length ? descId(activeIndex.value) : undefined));

function descId(index: number) {
  return `${inputId}_opt_${index}`;
}

function clampActive() {
  if (filtered.value.length === 0) {
    activeIndex.value = 0;
    return;
  }
  activeIndex.value = Math.max(0, Math.min(filtered.value.length - 1, activeIndex.value));
}

function select(cmd: CommandPaletteItem) {
  emit("select", cmd.id);
  emit("close");
}

function scrollActiveIntoView() {
  if (!filtered.value.length) return;
  const el = document.getElementById(descId(activeIndex.value));
  el?.scrollIntoView({ block: "nearest" });
}

function handleKeydown(event: KeyboardEvent) {
  if (event.key === "Escape") {
    event.preventDefault();
    emit("close");
    return;
  }
  if (event.key === "ArrowDown") {
    event.preventDefault();
    activeIndex.value += 1;
    clampActive();
    scrollActiveIntoView();
    return;
  }
  if (event.key === "ArrowUp") {
    event.preventDefault();
    activeIndex.value -= 1;
    clampActive();
    scrollActiveIntoView();
    return;
  }
  if (event.key === "Home") {
    event.preventDefault();
    activeIndex.value = 0;
    scrollActiveIntoView();
    return;
  }
  if (event.key === "End") {
    event.preventDefault();
    activeIndex.value = Math.max(0, filtered.value.length - 1);
    scrollActiveIntoView();
    return;
  }
  if (event.key === "Enter") {
    event.preventDefault();
    const cmd = filtered.value[activeIndex.value];
    if (cmd) select(cmd);
    return;
  }
}

onMounted(async () => {
  await nextTick();
  inputRef.value?.focus();
});

watch(
  () => query.value,
  () => {
    activeIndex.value = 0;
  }
);

watch(
  () => filtered.value.length,
  () => {
    clampActive();
  }
);
</script>

<style scoped>
.command-input {
  padding: 0.7rem 0.85rem;
}

.command-list {
  display: grid;
  gap: 0.5rem;
  max-height: 360px;
  overflow: auto;
}

.command-item {
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 0.75rem;
  align-items: center;
  padding: 0.7rem 0.8rem;
  border-radius: var(--radius-1);
  border: 1px solid var(--glass-border-0);
  background: var(--glass-0);
  color: var(--fg-0);
  text-align: left;
}

.command-item.is-active {
  border-color: color-mix(in srgb, var(--accent) 55%, transparent);
  box-shadow: var(--shadow-accent);
}

.command-item__icon {
  width: 34px;
  height: 34px;
  border-radius: var(--radius-1);
  display: grid;
  place-items: center;
  background: var(--glass-1);
  border: 1px solid var(--glass-border-1);
  font-size: 1.1rem;
}

.command-item__text {
  display: grid;
  gap: 0.15rem;
  min-width: 0;
}

.command-item__label {
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.command-item__desc {
  font-size: 0.85rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.command-item__hint {
  font-size: 0.7rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--fg-1);
}

.command-empty {
  padding: 0.85rem 0.8rem;
  border: 1px dashed var(--glass-border-0);
  border-radius: var(--radius-1);
}
</style>
